import { NotificationProps } from '../../Molecules/Notification/NotificationProps';

export type Notification = Omit<
  NotificationItemProps,
  'timeout' | 'onClose' | 'onExited' | 'autoHideDuration'
>;

export type NotificationType = Notification['kind'] | 'success' | 'notice';

export type EnqueueProps = {
  /** String, der die Art der Notification angibt. Standard ist `"neutral"`. */
  type?: Notification['kind'];
  /** String, der als Überschrift in der Notification angezeigt wird. */
  title?: Notification['title'];
  /** String, der als Text in der Notification angezeigt wird. */
  message: Notification['message'];
  /** String, der wenn angegeben, einen Link mit dem übergebenen Ziel um das Icon, Überschrift und Text spannt. */
  link?: Notification['link'];
  /** Boolean, der wenn `true`, die Notification davon abhält automatisch zu verschwinden. Standard ist `false`. */
  persist?: boolean;
};

export type Context = {
  /** Fügt neue Benachrichtigungen hinzu. */
  enqueue: (props: EnqueueProps) => Notification['id'];
  /** Schließt eine spezifische Benachrichtigung anhand ihrer ID. */
  close: (id: Notification['id']) => void;
};

export interface ProviderProps extends Partial<Pick<State, 'maxItems'>> {
  /** Initiale Benachrichtigungen lassen sich von außen in den `NotificationProvider` rein reichen. */
  items?: State['notifications'];
  /** Die Dauer in Millisekunden, nach der eine Benachrichtigung automatisch verschwindet. */
  autoHideDuration?: number;
}

export type State = {
  /** Die maximale Anzahl der auf einmal angezeigten Benachrichtigungen. */
  maxItems: number;
  /** Die derzeit aktiven und sichtbaren Benachrichtigungen. */
  notifications: Notification[];
  /** Die in die Warteschlange eingereihten Benachrichtigungen, die darauf warten, angezeigt zu werden. */
  queue: Notification[];
};

export enum ActionTypes {
  ADD_NOTIFICATION = 'ADD_NOTIFICATION',
  CLOSE_NOTIFICATION = 'CLOSE_NOTIFICATION',
  REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION',
}

export type Action =
  | { type: ActionTypes.ADD_NOTIFICATION; notification: Notification }
  | { type: ActionTypes.CLOSE_NOTIFICATION; id: Notification['id'] }
  | { type: ActionTypes.REMOVE_NOTIFICATION; id: Notification['id'] };

export interface NotificationItemProps
  extends Omit<NotificationProps, 'dmId' | 'icon'> {
  /** Unique ID. */
  id: string;
  /** Marks the notification as open. */
  open: boolean;
  /** Request the notification to get closed. This will create a timeout after which the notification will be closed and removed. */
  requestClose: boolean;
  /** The duration in which a notification is removed from the state. */
  autoHideDuration: number;
  /** Prevents the notification from disapearing automatically. */
  persist: boolean;
  /** The time it takes to transition the notification in and out. */
  timeout: number;
  /** Callback fired after the closing action was executed. Either manually by the user or after the specified `autoHideDuration`. */
  onClose: (id: string) => void;
  /** Callback fired after the notification was removed from the DOM.  */
  onExited: (id: string) => void;
}
